import React from 'react';
import { BadgeCheck, Crown, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toProfileSlug } from '../utils/slug';

export const VerificationBadge = ({ tier = 'free', size = 'sm' }) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };

  if (tier === 'powerhouse') {
    return <Crown className={`${sizeClasses[size]} text-[#FFD700] filter drop-shadow-[0_0_6px_rgba(255,215,0,0.6)]`} />;
  }
  
  if (tier === 'featured') {
    return <Sparkles className={`${sizeClasses[size]} text-[#FFD700] filter drop-shadow-[0_0_6px_rgba(255,215,0,0.6)]`} />;
  }

  return <BadgeCheck className={`${sizeClasses[size]} verified-badge`} />;
};

export const TierBadge = ({ tier }) => {
  const badges = {
    free: <span className="px-2 py-0.5 text-xs rounded tier-free">Verified</span>,
    licensed: <span className="px-2 py-0.5 text-xs rounded tier-licensed">Licensed Swapper</span>,
    featured: <span className="px-2 py-0.5 text-xs rounded tier-featured">Featured</span>,
    powerhouse: <span className="px-2 py-0.5 text-xs rounded tier-powerhouse">Powerhouse</span>
  };
  return badges[tier] || badges.free;
};

export const MatchScoreCard = ({ score, compact = false }) => {
  const getScoreColor = (s) => {
    if (s >= 80) return 'match-high';
    if (s >= 50) return 'match-medium';
    return 'match-low';
  };

  const getScoreLabel = (s) => {
    if (s >= 80) return 'Excellent Match';
    if (s >= 50) return 'Good Match';
    return 'Low Match';
  };

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        <span className={`font-data text-lg font-bold ${getScoreColor(score)}`}>{score}%</span>
        <span className="text-white/50 text-xs">match</span>
      </div>
    );
  }

  return (
    <div className="glass-card p-4 text-center">
      <div className="mb-2 text-white/50 text-sm">Match Score</div>
      <div className={`font-data text-4xl font-bold ${getScoreColor(score)}`}>
        {score}%
      </div>
      <div className={`text-sm mt-1 ${getScoreColor(score)}`}>
        {getScoreLabel(score)}
      </div>
    </div>
  );
};

export const PodcasterCard = ({ podcaster, showMatchScore = false, matchScore = null }) => {
  const getCardClass = () => {
    if (podcaster.subscription_tier === 'powerhouse') return 'powerhouse-border';
    if (podcaster.subscription_tier === 'featured') return 'featured-border';
    return '';
  };

  return (
    <Link 
      to={`/profile/${toProfileSlug(podcaster.name, podcaster.id)}`}
      className={`glass-card p-5 block transition-all duration-300 hover:scale-[1.02] ${getCardClass()}`}
      data-testid={`podcaster-card-${podcaster.id}`}
    >
      <div className="flex items-start gap-4">
        {/* Avatar */}
        <div className="relative">
          <img
            src={podcaster.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(podcaster.name)}&background=BD00FF&color=fff`}
            alt={podcaster.name}
            className="w-16 h-16 rounded-xl object-cover"
          />
          <div className="absolute -bottom-1 -right-1">
            <VerificationBadge tier={podcaster.subscription_tier} />
          </div>
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-white truncate">{podcaster.name}</h3>
            <TierBadge tier={podcaster.subscription_tier} />
          </div>
          <p className="text-[#00F3FF] text-sm font-medium truncate">{podcaster.podcast_name}</p>
          <p className="text-white/50 text-sm mt-1 line-clamp-2">{podcaster.description || 'No description yet'}</p>
          
          <div className="flex items-center gap-4 mt-3 text-xs text-white/50">
            <span className="font-data">
              <span className="text-white font-semibold">{(podcaster.monthly_listeners || 0).toLocaleString()}</span> listeners
            </span>
            <span className="font-data">
              <span className="text-white font-semibold">{podcaster.episodes_count || 0}</span> episodes
            </span>
            {podcaster.category && (
              <span className="px-2 py-0.5 rounded bg-white/5 text-white/70 capitalize">
                {podcaster.category.replace('_', ' ')}
              </span>
            )}
          </div>
        </div>

        {/* Match Score */}
        {showMatchScore && matchScore !== null && (
          <div className="text-right">
            <MatchScoreCard score={matchScore} compact />
          </div>
        )}
      </div>
    </Link>
  );
};

export const ActivityItem = ({ activity }) => {
  const getActivityIcon = (type) => {
    switch (type) {
      case 'match_accepted':
        return '🤝';
      case 'match_request':
        return '📩';
      case 'joined':
        return '🎙️';
      case 'subscription':
        return '⭐';
      default:
        return '📢';
    }
  };

  const getActivityText = (activity) => {
    switch (activity.activity_type) {
      case 'match_accepted':
        return <><strong>{activity.user_name}</strong> matched with <strong>{activity.target_name}</strong></>;
      case 'match_request':
        return <><strong>{activity.user_name}</strong> sent a match request</>;
      case 'joined':
        return <><strong>{activity.user_name}</strong> joined the directory</>;
      case 'subscription':
        return <><strong>{activity.user_name}</strong> upgraded their plan</>;
      default:
        return activity.details;
    }
  };

  const timeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  return (
    <div className="activity-item flex items-center gap-3 py-3 border-b border-white/5 last:border-0">
      <span className="text-xl">{getActivityIcon(activity.activity_type)}</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-white/80 truncate">
          {getActivityText(activity)}
        </p>
        <p className="text-xs text-[#00F3FF]">{activity.podcast_name}</p>
      </div>
      <span className="text-xs text-white/40 whitespace-nowrap">{timeAgo(activity.created_at)}</span>
    </div>
  );
};

export const LiveActivityFeed = ({ activities }) => {
  const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const HIDDEN = new Set(['quiz_answers', 'ai_prompt', 'profile_chatbot', 'profile_view']);
  const safe = (activities || []).filter((a) => {
    if (!a || HIDDEN.has(a.activity_type)) return false;
    if (typeof a.user_name === 'string' && UUID_RE.test(a.user_name.trim())) return false;
    if (typeof a.details === 'string' && a.details.trim().startsWith('{')) return false;
    return true;
  });
  return (
    <div className="glass-card p-4">
      <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-[#00FF94] animate-pulse"></span>
        Live Activity
      </h3>
      <div className="max-h-[400px] overflow-y-auto pr-2">
        {safe.length === 0 ? (
          <p className="text-white/50 text-sm text-center py-4">No recent activity</p>
        ) : (
          safe.map((activity, index) => (
            <ActivityItem key={activity.id || index} activity={activity} />
          ))
        )}
      </div>
    </div>
  );
};

export default {
  VerificationBadge,
  TierBadge,
  MatchScoreCard,
  PodcasterCard,
  ActivityItem,
  LiveActivityFeed
};
